import { NgClass } from '@angular/common';
import { Component, effect, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatCard } from '@angular/material/card';
import { MatRipple } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { BehaviorSubject, combineLatest, filter, Subscription } from 'rxjs';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { HttpUtilitiesService } from '../../../utilities/http-utilities.service';
import { EItemType } from '../../settings/fields-field-sets/field-sets/field-sets.interface';
import { IItemsFilterCardFields } from '../items-filter-card/items-filter-card.interface';
import { IBoardTreeStructure, TStatusCategory } from '../items.interface';
import { ItemsService } from '../items.service';
import { IKpiCardBreakdownDetail, IKpiCardDetail, TKpiCard, TKpiCardKey } from './items-kpi-cards.interface';
import { IItemsKpiInformation, ItemsKpiCardsStore } from './items-kpi-cards.store';

const MINIMUM_VISIBLE_BAR_SHARE = 3;

@Component({
  imports: [MatIcon, TranslateModule, MatCard, NgClass, MatRipple],
  providers: [ItemsKpiCardsStore, HttpUtilitiesService],
  selector: 'app-items-kpi-cards',
  styleUrls: ['items-kpi-cards.component.scss'],
  templateUrl: './items-kpi-cards.component.html',
})
export class ItemsKpiCardsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public filterCardData$ = new BehaviorSubject<Partial<IItemsFilterCardFields> | null>(null);
  @Input() public boardTreeStructure: IBoardTreeStructure[] = [];

  @Output() public kpiCardSelected = new EventEmitter<TKpiCardKey | null>();

  public selectedKpiCard: TKpiCardKey | null = null;
  public readonly kpiCardOrder: TKpiCardKey[] = ['total', 'todo', 'inProgress', 'done', 'escalated'];
  public kpiCardDetails: TKpiCard = {
    done: {
      breakdown: this.createItemTypeBreakdown(),
      count: 0,
      icon: 'task_alt',
      iconClass: 'kpi-card-icon-done',
      title: this.translate.instant('page.items.label.done'),
    },
    escalated: {
      breakdown: this.createItemTypeBreakdown(),
      count: 0,
      icon: 'arrow_upward',
      iconClass: 'kpi-card-icon-escalated',
      title: this.translate.instant('page.items.escalation.escalated'),
    },
    inProgress: {
      breakdown: this.createItemTypeBreakdown(),
      count: 0,
      icon: 'schedule',
      iconClass: 'kpi-card-icon-in-progress',
      title: this.translate.instant('page.items.label.inProgress'),
    },
    todo: {
      breakdown: this.createItemTypeBreakdown(),
      count: 0,
      icon: 'adjust',
      iconClass: 'kpi-card-icon-todo',
      title: this.translate.instant('page.items.label.todo'),
    },
    total: {
      breakdown: this.createItemTypeBreakdown(),
      count: 0,
      icon: 'checklist',
      iconClass: 'kpi-card-icon-total',
      title: this.translate.instant('page.items.label.totalItems'),
    },
  };
  private readonly subscriptions: Subscription[] = [];

  constructor(
    public readonly store: ItemsKpiCardsStore,
    private readonly itemsService: ItemsService,
    private readonly translate: TranslateService,
  ) {
    effect(() => {
      this.kpiCardDetails = this.formatKpiInformation(this.store.kpiInformation());
    });
  }

  public selectKpiCard(kpiCardKey: TKpiCardKey): void {
    this.selectedKpiCard = this.selectedKpiCard === kpiCardKey ? null : kpiCardKey;
    this.kpiCardSelected.emit(this.selectedKpiCard);
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      }).subscribe(({ filterCard }) => {
        this.selectedKpiCard = this.deriveSelectedKpiCard(filterCard as IItemsFilterCardFields);
        this.loadItems(filterCard as IItemsFilterCardFields);
      }),
      this.itemsService.refreshItems$.subscribe(() => {
        this.loadItems(this.filterCardData$.value as IItemsFilterCardFields);
      }),
    );
  }

  /**
   * `'escalated'` takes priority (an item can be both escalated and, say, `'todo'` at once — see
   * `formatKpiInformation`'s comment on `escalated` being a flag, not a status), and `'total'` is
   * the fallback whenever neither `itemScope` nor `workflowStep` picks out a more specific card —
   * exactly the state `applyKpiCardFilter`'s `null`/`'total'` cases leave behind, so toggling a card
   * off (or clicking "Total" itself) lands back on `'total'` rather than no highlight at all.
   */
  private deriveSelectedKpiCard(filterCard: IItemsFilterCardFields): TKpiCardKey | null {
    if (filterCard.itemScope === 'escalated') {
      return 'escalated';
    }

    return this.matchingStatusCategory(filterCard.workflowStep) ?? null;
  }

  /** Matches a status KPI card only when every selected step shares that category — mirrors how
   * `ItemsFilterCardService.applyStatusCategory` sets the field: all steps of one category at once,
   * never a mix. */
  private matchingStatusCategory(workflowStep: IDropdownOption[] | null | undefined): TStatusCategory | null {
    const [first, ...rest] = workflowStep ?? [];

    if (!first || rest.some((step) => step.statusCategory !== first.statusCategory)) {
      return null;
    }

    return first.statusCategory as 'todo' | 'inProgress' | 'done';
  }

  private loadItems(filterCard: IItemsFilterCardFields): void {
    this.store.loadItems({
      additionalCustomSearch: this.itemsService.prepareAdditionalCustomSearch(filterCard, this.boardTreeStructure),
      filters: [...this.itemsService.prepareFilters(filterCard)],
    });
  }

  private createItemTypeBreakdown(): IKpiCardBreakdownDetail[] {
    return [
      {
        barClass: 'kpi-card-tone-issue',
        count: 0,
        key: EItemType.issue,
        share: 0,
        title: this.translate.instant('page.items.button.issues'),
      },
      {
        barClass: 'kpi-card-tone-action',
        count: 0,
        key: EItemType.action,
        share: 0,
        title: this.translate.instant('page.items.button.actions'),
      },
    ];
  }

  private formatKpiInformation(kpiInformation: IItemsKpiInformation[]): TKpiCard {
    this.resetKpiCardCounts();

    const kpiCardDetails: TKpiCard = kpiInformation.reduce((acc: TKpiCard, kpiInfo: IItemsKpiInformation) => {
      const itemType: EItemType = kpiInfo.boardItemConfigurationItemType;

      this.addToKpiCard(acc.total, itemType, kpiInfo.count);
      this.addToKpiCard(acc[kpiInfo.currentWorkflowStepStatusCategory], itemType, kpiInfo.count);
      // Escalated items are also counted in their status card, so this is a flag, not a status.
      this.addToKpiCard(acc.escalated, itemType, kpiInfo.escalatedCount);

      return acc;
    }, this.kpiCardDetails);

    this.calculateBreakdownShares(kpiCardDetails);

    return kpiCardDetails;
  }

  private addToKpiCard(kpiCardDetail: IKpiCardDetail, itemType: EItemType, count: number): void {
    const itemTypeBreakdown: IKpiCardBreakdownDetail | undefined = kpiCardDetail.breakdown.find(
      (breakdown: IKpiCardBreakdownDetail) => breakdown.key === itemType,
    );

    if (!isNil(itemTypeBreakdown)) {
      itemTypeBreakdown.count += count;
    }

    kpiCardDetail.count += count;
  }

  private calculateBreakdownShares(kpiCardDetails: TKpiCard): void {
    this.kpiCardOrder.forEach((kpiCardKey: TKpiCardKey) => {
      const { breakdown, count }: IKpiCardDetail = kpiCardDetails[kpiCardKey];

      breakdown.forEach((itemTypeBreakdown: IKpiCardBreakdownDetail) => {
        itemTypeBreakdown.share =
          itemTypeBreakdown.count === 0
            ? 0
            : Math.max(Math.round((itemTypeBreakdown.count / count) * 100), MINIMUM_VISIBLE_BAR_SHARE);
      });
    });
  }

  private resetKpiCardCounts(): void {
    this.kpiCardOrder.forEach((kpiCardKey: TKpiCardKey) => {
      this.kpiCardDetails[kpiCardKey].count = 0;
      this.kpiCardDetails[kpiCardKey].breakdown.forEach((breakdown: IKpiCardBreakdownDetail) => {
        breakdown.count = 0;
      });
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }
}
